import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const UnescoIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  useEffect(() => { if (visible) fetchMappings(); }, [visible]);

  const [unescoItems, setUnescoItems] = useState([]);
  const [unescoSearch, setUnescoSearch] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (visible) {
      fetchUnescoItems('');
      setUnescoSearch('');
    }
  }, [visible]);

  const fetchUnescoItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=100');
      const url = `/external_api/unesco/get-all-unesco-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data || res.data?.items || [];
      setUnescoItems(Array.isArray(items) ? items : []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchUnescoItems(unescoSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [unescoSearch]);

  const fetchMappings = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/unesco/indicator-mappings');
      setMappings(res.data?.mappings || res.data || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        unesco_indicator_code: record.unesco_indicator_code,
        unesco_topic: record.unesco_topic,
        unesco_indicator_name: record.unesco_indicator_name,
      });
      if (record.unesco_indicator_code) {
        const exists = unescoItems.find(i => String(i.unesco_indicator_code) === String(record.unesco_indicator_code));
        if (!exists) {
          setUnescoItems(prev => [{ unesco_indicator_code: record.unesco_indicator_code, unesco_indicator_name: record.unesco_indicator_name, unesco_topic: record.unesco_topic }, ...prev]);
        }
      }
    }
    if (!visible && !record) form.resetFields();
  }, [visible, record, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/unesco/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/unesco/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally { setLoading(false); }
  };

  const validateUnique = (field, val) => {
    if (!val) return true;
    return !mappings.some(m => String(m[field] || '') === String(val) && (!record || String(m.id) !== String(record.id)));
  };

  return (
    <Modal
      title={record ? 'Edit UNESCO Indicator Mapping' : 'Add UNESCO Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="oic_ind_code" label="OIC Indicator" rules={[{ required: true, message: 'Please select an OIC indicator' }]}>
          <Select showSearch autoClearSearchValue={false} allowClear placeholder="Select an indicator" onChange={(val) => {
            const sel = availableIndicators.find(i => String(i.ind_code) === String(val));
            if (sel) form.setFieldsValue({ oic_ind_name: sel.ind_name_eng });
          }} optionFilterProp="children">
            {availableIndicators.map(ind => (<Option key={String(ind.ind_code)} value={String(ind.ind_code)}>{ind.ind_code} - {ind.ind_name_eng}</Option>))}
          </Select>
        </Form.Item>

        <Form.Item
          name="unesco_indicator_code"
          label="UNESCO Indicator"
          rules={[{ required: true, message: 'Please select a UNESCO indicator' }, { validator: (_, val) => {
            if (!val) return Promise.resolve();
            if (!validateUnique('unesco_indicator_code', val)) return Promise.reject(new Error('UNESCO indicator code already mapped'));
            return Promise.resolve();
          } }] }
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select a UNESCO indicator"
            optionFilterProp="children"
            filterOption={false}
            onSearch={(val) => setUnescoSearch(val)}
            onChange={(val) => {
              const sel = unescoItems.find(i => String(i.unesco_indicator_code || i.unesco_indicator) === String(val));
              if (sel) {
                form.setFieldsValue({
                  unesco_indicator_code: sel.unesco_indicator_code || sel.unesco_indicator,
                  unesco_topic: sel.unesco_topic || sel.topic || null,
                  unesco_indicator_name: sel.unesco_indicator_name || sel.name || null,
                });
              }
            }}
          >
            {unescoItems.map(it => (
              <Option key={it.unesco_indicator_code || it.unesco_indicator} value={it.unesco_indicator_code || it.unesco_indicator}>
                {(it.unesco_indicator_code || it.unesco_indicator) + ' - ' + (it.unesco_indicator_name || it.name)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="unesco_topic" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="unesco_indicator_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UnescoIndicatorMappingModal;
